import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LinkedInSidebar from '../LinkedInSidebar';
import { Heart, Eye } from "lucide-react";
import axios from "axios";

const TermsPage = ({ currentPage, setCurrentPage, setTitleDescription }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setTitleDescription("Fractofolio - Privacy Policy", "The future of investment networking! Discover market insights, and make informed investment decisions with our community.")
  }, []);
  
  return (
    <div className="linkedin-layout bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="hidden lg:block lg:col-span-1">
            <LinkedInSidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </div>
          
          <div className="lg:col-span-3 space-y-4">
            {/* Hero Post */}
            <div className="linkedin-card">
              <div className="linkedin-card-header">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">FR</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Privacy Policy</h3>
                    <p className="text-sm text-gray-600">Effective Date: 15 March 2025</p>
                  </div>
                </div>
              </div>
              
              <div className="linkedin-card-content">
                <div className="text-sm">
                  <p className="font-regular">At Fractofolio, we are committed to protecting your privacy and ensuring that your personal information is handled safely and responsibly. This Privacy Policy outlines the types of information we collect, how we use and protect it, and your rights regarding your data when using our website and services.</p>

                  <h2 className="mt-3 font-bold">1. Information We Collect</h2>
                  <p className="font-regular">We may collect the following types of information when you use our website:</p>
                  <ul className="font-regular">
                      <li><strong>Personal Information</strong>: When you register or interact with our platform, we may collect your name, email address, and other personal details that you provide.</li>
                      <li><strong>Usage Data</strong>: We collect information about how you use our website, such as pages visited, links clicked, and other actions taken. This helps us improve our services and offer a better user experience.</li>
                      <li><strong>Cookies and Tracking Technologies</strong>: We use cookies and other tracking technologies to enhance your experience on our site, such as remembering your preferences and analyzing site traffic.</li>
                  </ul>

                  <h2 className="mt-3 font-bold">2. How We Use Your Information</h2>
                  <p className="font-regular">We use the information we collect for the following purposes:</p>
                  <ul className="font-regular">
                      <li>To provide, maintain, and improve our platform and services.</li>
                      <li>To personalize your experience and deliver relevant content.</li>
                      <li>To communicate with you, including sending updates, newsletters, or responses to inquiries.</li>
                      <li>To monitor and analyze usage and trends to improve the website and services.</li>
                      <li>To ensure compliance with legal obligations and resolve disputes.</li>
                  </ul>

                  <h2 className="mt-3 font-bold">3. Sharing Your Information</h2>
                  <p className="font-regular">We do not sell or rent your personal information to third parties. However, we may share your information with trusted third-party service providers who assist us in operating our website, conducting business, or delivering services, provided they agree to keep this information confidential.</p>

                  <p className="font-regular">We may also share your data in the following circumstances:</p>
                  <ul className="font-regular">
                      <li>If required by law, regulation, or legal process (e.g., a subpoena).</li>
                      <li>To protect the safety, rights, or property of Fractofolio, our users, or others.</li>
                      <li>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new owner.</li>
                  </ul>

                  <h2 className="mt-3 font-bold">4. Data Security</h2>
                  <p className="font-regular">We implement reasonable administrative, technical, and physical measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, please note that no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee the absolute security of your data.</p>

                  <h2 className="mt-3 font-bold">5. Your Data Rights</h2>
                  <p className="font-regular">Depending on your location and applicable laws, you may have the following rights regarding your personal information:</p>
                  <ul className="font-regular">
                      <li><strong>Access</strong>: You can request access to the personal information we hold about you.</li>
                      <li><strong>Correction</strong>: You can request that we correct any inaccurate or incomplete information.</li>
                      <li><strong>Deletion</strong>: You can request the deletion of your personal data, subject to certain exceptions (e.g., legal obligations).</li>
                      <li><strong>Opt-out of Communications</strong>: You can opt-out of receiving marketing emails by following the unsubscribe instructions provided in each email or by contacting us directly.</li>
                  </ul>
                  <p className="font-regular">To exercise your rights, please contact us at info@fractofolio.com.</p>

                  <h2 className="mt-3 font-bold">6. Cookies and Tracking Technologies</h2>
                  <p className="font-regular">Cookies are small text files that are placed on your device when you visit our website. These cookies help us remember your preferences, improve functionality, and analyze site usage.</p>
                  <p className="font-regular">You can control cookies through your browser settings. However, please note that disabling cookies may affect the functionality of the website.</p>
                  {/* <p className="font-regular">For more information on how we use cookies, please see our [Cookie Policy] (if applicable).</p> */}

                  <h2 className="mt-3 font-bold">7. Third-Party Links</h2>
                  <p className="font-regular">Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites before providing personal information.</p>

                  <h2 className="mt-3 font-bold">8. Children’s Privacy</h2>
                  <p className="font-regular">Our services are not intended for use by individuals under the age of 18. We do not knowingly collect or solicit personal information from children. If we become aware that we have inadvertently collected personal information from a child under 18, we will take steps to delete such information.</p>

                  <h2 className="mt-3 font-bold">9. Changes to This Privacy Policy</h2>
                  <p className="font-regular">We may update this Privacy Policy from time to time. When we make changes, we will post the updated policy on this page and update the effective date at the top of the page. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.</p>

                  <h2 className="mt-3 font-bold">10. Contact Us</h2>
                  <p className="font-regular">If you have any questions or concerns about this Privacy Policy or how we handle your personal information, please contact us at:</p>
                  <p className="font-regular">Fractofolio</p>
                  <p className="font-regular">info@fractofolio.com</p>
                  <p className="font-regular">C. 118B, San José, San Rafael, Trejos Montealegre, 10203, Costa Rica</p>

                  <p className="mt-3 font-regular">This Privacy Policy was last updated on 15 March 2025.</p>
                </div>
              </div>
              
              <div className="linkedin-card-actions">
                {/* <button className="linkedin-action-btn">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Like
                </button> */}
                {/* <CommentSection postId="hero" /> */}
                {/* <button className="linkedin-action-btn">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share
                </button> */}
              </div>
              {/* <CommentSectionExpanded postId="hero" /> */}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default TermsPage;